<script setup lang="ts">
import { computed, watch } from 'vue'
import { useUiStore } from '@/stores/ui'
import AppHeader from './AppHeader.vue'
import AppSidebar from './AppSidebar.vue'
import MobileNav from './MobileNav.vue'
import VoiceButton from '@/components/modes/VoiceButton.vue'
import SahbiAvatar from '@/components/modes/SahbiAvatar.vue'

const uiStore = useUiStore()

const sidebarOpen = computed(() => uiStore.sidebarOpen)
const isMobile = computed(() => uiStore.isMobile)
const isWordless = computed(() => uiStore.isWordless)
const isIntent = computed(() => uiStore.isIntent)
const currentMode = computed(() => uiStore.mode)

// Apply mode class to document
watch(currentMode, (mode) => {
  document.body.classList.remove('wordless-mode', 'intent-mode', 'classic-mode')
  document.body.classList.add(`${mode}-mode`)
}, { immediate: true })
</script>

<template>
  <div class="min-h-screen flex flex-col" :class="{ 'wordless-mode': isWordless, 'intent-mode': isIntent }">
    <!-- Header -->
    <AppHeader />

    <div class="flex flex-1 pt-16">
      <!-- Sidebar - Desktop (fixed position) -->
      <aside
        v-if="!isMobile"
        :class="[
          'fixed left-0 top-16 bottom-0 z-20 flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 overflow-y-auto',
          sidebarOpen ? 'w-64' : 'w-20'
        ]"
      >
        <AppSidebar :collapsed="!sidebarOpen" />
      </aside>

      <!-- Sidebar - Mobile overlay -->
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-300"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isMobile && sidebarOpen"
          class="fixed inset-0 bg-black/50 z-40"
          @click="uiStore.closeSidebar"
        />
      </Transition>

      <Transition
        enter-active-class="transition-transform duration-300"
        enter-from-class="-translate-x-full"
        enter-to-class="translate-x-0"
        leave-active-class="transition-transform duration-300"
        leave-from-class="translate-x-0"
        leave-to-class="-translate-x-full"
      >
        <aside
          v-if="isMobile && sidebarOpen"
          class="fixed left-0 top-16 bottom-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700"
        >
          <AppSidebar :collapsed="false" />
        </aside>
      </Transition>

      <!-- Main content - with margin to account for fixed sidebar -->
      <main
        :class="[
          'flex-1 p-4 md:p-6 overflow-auto bg-gray-50 dark:bg-gray-900 min-h-[calc(100vh-4rem)]',
          !isMobile && sidebarOpen ? 'ml-64' : '',
          !isMobile && !sidebarOpen ? 'ml-20' : ''
        ]"
      >
        <div class="max-w-7xl mx-auto">
          <slot />
        </div>
      </main>
    </div>

    <!-- Mobile bottom navigation -->
    <MobileNav v-if="isMobile" />

    <!-- Sahbi Avatar for Wordless mode -->
    <Transition
      enter-active-class="transition-all duration-300"
      enter-from-class="opacity-0 scale-75"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition-all duration-300"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-75"
    >
      <SahbiAvatar
        v-if="isWordless"
        class="fixed bottom-24 right-6 z-40"
        size="lg"
      />
    </Transition>

    <!-- Voice Button for Intent mode -->
    <Transition
      enter-active-class="transition-all duration-300"
      enter-from-class="opacity-0 translate-y-10"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-300"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-10"
    >
      <VoiceButton v-if="isIntent" />
    </Transition>
  </div>
</template>
